'use client'

/**
 * HomeHeaderContainer - Special header wrapper for the home page only
 *
 * WHY THIS EXISTS (vs HeaderContainer):
 * The home page has a full-bleed hero slideshow. The header needs to:
 * 1. Start TRANSPARENT so it floats over the hero images
 * 2. Become OPAQUE (solid white) when the user scrolls past the hero
 * 3. Become OPAQUE immediately when mobile menu or search opens
 *
 * This is different from HeaderContainer which is always solid white.
 *
 * CSS classes applied:
 * - 'home-header' (always) - different base styles than standard 'header'
 * - 'collapsed' - when scrolled on mobile (shrinks header)
 * - 'opaque' - when header should be solid white (scroll/menu/search)
 *
 * See also: HeaderContainer.js, (home)/layout.js
 */

import { useState, useEffect } from 'react'
import { useAppContext } from '@/app/_lib/_context'

const HomeHeaderContainer = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false)
    const [opaque, setOpaque] = useState(false)
    const { mobileMenuOpen, activeSearch } = useAppContext()

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY
            const isDesktop = window.innerWidth >= 1024

            // On home page, both opaque and collapsed happen together after hero is scrolled off
            const threshold = window.innerHeight - 275

            if (isDesktop) {
                if (collapsed) setCollapsed(false)
                if (opaque) setOpaque(false)
            } else {
                // Both transitions at same threshold with hysteresis
                if (collapsed) {
                    const isActive = scrollY > threshold - 50
                    setCollapsed(isActive)
                    setOpaque(isActive)
                } else {
                    const isActive = scrollY > threshold
                    setCollapsed(isActive)
                    setOpaque(isActive)
                }
            }
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        window.addEventListener('resize', handleScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', handleScroll)
        }
    }, [collapsed])

    // Sync collapsed state to body class for components outside header
    useEffect(() => {
        if (collapsed) {
            document.body.classList.add('header-collapsed')
        } else {
            document.body.classList.remove('header-collapsed')
        }
    }, [collapsed])

    // Determine if header should be opaque (scroll-based OR mobile menu open OR search open)
    const isOpaque = opaque || mobileMenuOpen || activeSearch

    return (
        <header className={`group/header home-header ${collapsed ? 'collapsed' : ''} ${isOpaque ? 'opaque' : ''}`}>
            {children}
        </header>
    )
}

export default HomeHeaderContainer
